import React from 'react';

import { Field, Form } from 'react-final-form';
import { useSelector } from 'react-redux';

import ArrayFields from 'components/shared/ArrayFields';
import FormActions from 'components/shared/FormAdapters/FormDefault/FormActions';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import FormGroup from 'components/shared/FormAdapters/FormDefault/FormGroup/Base';
import Label from 'components/shared/FormAdapters/FormDefault/Label';
import Loader from 'components/shared/Loader';
import StickyFooter from 'components/shared/StickyFooter/StickyFooter';
import FormTitle from 'components/shared/Typography/FormTitle';
import UnsavedChangesFormWatcher from 'components/shared/UnsavedChangesFormWatcher';
import { ENTER_VIDEO_HOST_LINK } from 'constants/texts';
import useElementBottomPosition, { SNACKBAR_POSITION } from 'hooks/useElementBottomPosition';
import { useScrollPageToTop } from 'hooks/useScrollPageToTop';
import { pageDataSelector } from 'store/page/selectors';

import { BuilderFooterButton } from '../../shared/BuilderFooterButton';
import { usePageContext } from '../../shared/context';
import { useSaveErrorHandler } from '../../shared/hooks/useSaveErrorHandler';
import PageFormButtons from '../../shared/PageFormButtons';
import { useResources } from './functions';
import Sheets from './Upload/Sheets';

function Resources() {
  useScrollPageToTop();
  useElementBottomPosition(SNACKBAR_POSITION);
  const page = useSelector(pageDataSelector);
  const { useNextStepFormHandler, useSavePageHandler } = usePageContext();
  const handleSaveError = useSaveErrorHandler({ needToRedirect: true });
  const handleNextStep = useNextStepFormHandler({ onNextError: handleSaveError });
  const handleSave = useSavePageHandler({ onSaveError: handleSaveError });

  const { data, openModal, onDelete, validate, onAfterEdited } = useResources({ page });

  return (
    <Form
      onSubmit={handleNextStep}
      initialValues={data}
      validate={validate}
      render={({ handleSubmit, submitting, invalid, values, form }) => (
        <form onSubmit={handleSubmit} autoComplete="nope">
          <UnsavedChangesFormWatcher />

          {submitting && <Loader isFillBlock />}
          <FormTitle center>Agent Internal Resources</FormTitle>
          <FormTitle hint="Upload or search for relevant rate and info sheets for this property. ">
            I. Rate & Info Sheets
          </FormTitle>

          <Sheets
            files={values.documents}
            form={form}
            onOpenModal={openModal}
            onDelete={onDelete}
            onAfterEdited={onAfterEdited}
            name="documents"
          />

          <FormTitle hint="Enter any helpful video/webinar links for Agents to have access to in their library.">
            II. Videos & Webinars
          </FormTitle>

          <FormGroup>
            <Label withBorder>Videos</Label>
          </FormGroup>

          <ArrayFields
            initialValues={data}
            data={{
              sectionName: 'videos',
              config: { twoColumn: true },
              fieldsData: [
                {
                  name: 'name',
                  placeholder: 'Video Name',
                },
                {
                  name: 'link',
                  placeholder: ENTER_VIDEO_HOST_LINK,
                },
              ],
            }}
          />

          <FormGroup>
            <Label withBorder>Webinars</Label>
          </FormGroup>

          <ArrayFields
            initialValues={data}
            data={{
              sectionName: 'webinars',
              config: { twoColumn: true },
              fieldsData: [
                {
                  name: 'name',
                  placeholder: 'Webinar Video',
                },
                {
                  name: 'link',
                  placeholder: ENTER_VIDEO_HOST_LINK,
                },
              ],
            }}
          />

          <FormTitle hint="Enter a live availability link for Agents to have access to availability in real time.">
            III. Live Availability
          </FormTitle>

          <Field
            component={FormGroupAdapter}
            label="Link"
            name="bookingUrl"
            placeholder="http://www.website.com"
            type="text"
            parse={(value) => (value === '' ? null : value)}
          />
          <Field
            component={FormGroupAdapter}
            label="Instructions"
            name="bookingDescription"
            placeholder="Login info"
            type="textarea"
            minRows={5}
            parse={(value) => (value === '' ? null : value)}
          />

          <StickyFooter>
            <FormActions isRight>
              <BuilderFooterButton id="ResourcesNextButton" htmlType="submit" disabled={submitting || invalid}>
                Next
              </BuilderFooterButton>
            </FormActions>
          </StickyFooter>

          <PageFormButtons onSave={handleSave} />
        </form>
      )}
    />
  );
}

export default Resources;
